import React, { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    Grid,
    TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import CheckboxList from 'components/common/CheckboxList';
import { ModalActionProps } from 'models/AutoModals';
import { CarModel } from 'models/StockModel';
import { exportCertificateRequired } from 'modules/cars/utils/common';
import carListFamily from 'recoil/atoms/carListFamily';


type ExpCertTrackDialogProps = {
    car: CarModel,
} & ModalActionProps<{
    ids: number[],
    originalSentAt: string,
    trackCode: string,
    courier: string,
}>

const ExpCertTrackDialog = ({car, onFinish}: ExpCertTrackDialogProps) => {
    const {t} = useTranslation('cars');
    const cars = useRecoilValue(carListFamily('pending'));
    const sameCompanyCars = useMemo(() => cars.filter(c => exportCertificateRequired(c) &&
        c.yard?.portId === 1 &&
        c.company.id === car.company.id &&
        !c.exportCertificate?.trackCode &&
        !c.exportCertificate?.originalArrivedAt
    ), [car.company.id, cars]);
    const [carsChecked, setCarsChecked] = useState(sameCompanyCars.map(c => c.id === car.id));
    const [date, setDate] = useState<DateTime|null>(null);

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const data = Object.fromEntries(new FormData(event.currentTarget).entries()) as any;

        onFinish({
            ...data,
            ids: sameCompanyCars.filter((_, i) => carsChecked[i]).map(c => c.id),
            originalSentAt: date?.toISODate(),
        });
    };

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <DatePicker
                            label={t('sent_at')}
                            format="yyyy-MM-dd"
                            slotProps={{ textField: { fullWidth: true, required: true } }}
                            onChange={d => setDate(d!)}
                            value={date}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="trackCode"
                            name="trackCode"
                            label={t('track_code')}
                            fullWidth
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id="courier"
                            name="courier"
                            label={t('courier')}
                            fullWidth
                            required
                        />
                    </Grid>
                </Grid>

                <CheckboxList
                    items={sameCompanyCars}
                    formatter={c => c.chassisNumber}
                    checked={carsChecked}
                    setChecked={setCarsChecked}
                />
            </DialogContent>

            <DialogActions>
                <Button type="submit">{t('add')}</Button>
            </DialogActions>
        </Box>
    );
};

export default ExpCertTrackDialog;
